import { ThemeProvider, useTheme } from '@mui/material/styles';
import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { TSelectedSubscriptionInfo, TSubScriptionState } from 'typings';
import {
  resolveThemeProduct,
  withProduct,
  type TProductName
} from './tokens/productTheme';

/**
 * Themes a subtree around one product.
 *
 * Wrap a product's screens once and everything inside — MUI components
 * included — picks up that product's colour, page tint and gradient.
 *
 * @example
 *   <ProductThemeProvider product="pet">
 *     <PetPlanComparison />
 *   </ProductThemeProvider>
 */

const ProductContext = createContext<TProductName>('home');

/** Which product the surrounding subtree is themed to. */
export const useProduct = (): TProductName => useContext(ProductContext);

type Props = {
  product: TProductName;
  children: React.ReactNode;
};

function ProductThemeProvider({ product, children }: Props) {
  const parent = useTheme();
  // Memoised: createTheme is not free, and this sits above whole pages.
  const theme = useMemo(() => withProduct(parent, product), [parent, product]);

  return (
    <ProductContext.Provider value={product}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ProductContext.Provider>
  );
}

/**
 * Themes the whole app from the URL.
 *
 * The route table is product-segmented, so one of these mounted above the
 * routes gives every page its product's background, primary colour and
 * gradient — no per-page wiring, and new /pet/* or /bird/* routes are covered
 * the moment they are added.
 *
 * Also folds in the app-bridge/Redux fallback chain (resolveThemeProduct)
 * that PrivateRoute used to apply on its own, deeper in the tree — doing it
 * here means the theme is fully resolved before CssBaseline and the
 * Suspense fallback loader paint, instead of painting Home first and
 * snapping to the real product once the lazy page mounts.
 *
 * A page that knows its product from state rather than the URL can still
 * override by nesting a <ProductThemeProvider product={...}> inside.
 */
export function RouteProductTheme({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { selectedSubscriptionInfo } = useAppSelector<TSubScriptionState>(
    (state) => state.subscription
  );
  const product = resolveThemeProduct(
    pathname,
    (selectedSubscriptionInfo as TSelectedSubscriptionInfo)?.planSelected
      ?.product_id
  );

  return (
    <ProductThemeProvider product={product}>{children}</ProductThemeProvider>
  );
}

export default ProductThemeProvider;
